/* eslint-disable */
import React, { useEffect, useState, useRef } from "react";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import config from "../../config";
import { useParams } from "react-router-dom";
import useUserContext from "../../hooks/useUserContext";

export default function NHBCateringForm({ document }) {
    const params = useParams();
    const {
        userState: { accessToken },
    } = useUserContext();

    const [termsData, setTermsData] = useState(document || null);
    const contentRef = useRef(null);

    // Fetch the document if not provided as prop
    useEffect(() => {
        if (document) return;

        const fetchCateringData = async () => {
            try {
                const response = await fetch(
                    `${config.API_BASE_URL}/api/nhb/documents/${params.documentId}`,
                    {
                        headers: { Authorization: `Bearer ${accessToken}` },
                    }
                );

                if (!response.ok) throw new Error("Failed to fetch NHB Catering document");
                const data = await response.json();

                // Merge root and nested nhbCatering/nhbTerms
                const merged = data?.data?.nhbCatering
                    ? { ...data.data, ...data.data.nhbCatering }
                    : data.data;

                setTermsData(merged);
            } catch (error) {
                console.error("Error fetching NHB Catering document:", error);
            }
        };

        if (params.documentId && accessToken) fetchCateringData();
    }, [params.documentId, accessToken, document]);

    const ReadOnlyCateringForm = ({ cateringSelections }) => {
        if (!cateringSelections) return null;

        const renderCheckbox = (checked, label, description = "") => (
            <div className="flex gap-3 items-start">
                <input
                    type="checkbox"
                    checked={!!checked}
                    readOnly
                    className="size-4 rounded border-gray-300 bg-white text-green-600 cursor-default focus:ring-0"
                />
                <div className="text-sm text-gray-800">
                    <p className="font-medium">{label}</p>
                    {description && <p className="text-gray-500">{description}</p>}
                </div>
            </div>
        );

        // Helper to check if a section has any true values
        const hasAnyTrue = (obj) => {
            if (!obj) return false;
            return Object.values(obj).some((val) => {
                if (typeof val === "object") return hasAnyTrue(val);
                return !!val;
            });
        };

        return (
            <div className="space-y-10">
                {/* --- SALADS --- */}
                <fieldset>
                    <legend className="text-xl font-semibold text-gray-900">Salads</legend>
                    <div className="mt-6 space-y-6 ml-8">
                        {hasAnyTrue(cateringSelections.salads) ? (
                            <>
                                {/* HOUSE SALAD */}
                                {renderCheckbox(
                                    cateringSelections?.salads?.houseSalad,
                                    `HOUSE SALAD - $75${cateringSelections?.salads?.houseSaladAddChicken ? " (+ Chicken +$60)" : ""
                                    }${cateringSelections?.salads?.houseSaladAddSalmon ? " (+ Salmon +$80)" : ""
                                    }`,
                                    "spring mix, grape tomatoes, cucumbers, parmesan, balsamic vinaigrette, spiced sunflower seeds"
                                )}
                                <div className="ml-10 space-y-2">
                                    {renderCheckbox(
                                        cateringSelections?.salads?.houseSaladAddChicken,
                                        "Add Chicken +$60"
                                    )}
                                    {renderCheckbox(
                                        cateringSelections?.salads?.houseSaladAddSalmon,
                                        "Add Salmon +$80"
                                    )}
                                </div>

                                {/* CAESAR SALAD */}
                                {renderCheckbox(
                                    cateringSelections?.salads?.caesarSalad,
                                    `CAESAR SALAD - $70${cateringSelections?.salads?.caesarSaladAddChicken ? " (+ Chicken +$60)" : ""
                                    }${cateringSelections?.salads?.caesarSaladAddSalmon ? " (+ Salmon +$80)" : ""
                                    }`,
                                    "romaine lettuce, herb garlic croutons, parmesan, creamy peppercorn caesar dressing"
                                )}
                                <div className="ml-10 space-y-2">
                                    {renderCheckbox(
                                        cateringSelections?.salads?.caesarSaladAddChicken,
                                        "Add Chicken +$60"
                                    )}
                                    {renderCheckbox(
                                        cateringSelections?.salads?.caesarSaladAddSalmon,
                                        "Add Salmon +$80"
                                    )}
                                </div>

                                {/* ORCHARD SALAD */}
                                {renderCheckbox(
                                    cateringSelections?.salads?.orchardSalad,
                                    `ORCHARD SALAD - $85${cateringSelections?.salads?.orchardSaladAddChicken ? " (+ Chicken +$60)" : ""
                                    }${cateringSelections?.salads?.orchardSaladAddSalmon ? " (+ Salmon +$80)" : ""
                                    }`,
                                    "lettuce mix, fuji apples, candied almonds, bacon, goat cheese, lemon vinaigrette"
                                )}
                                <div className="ml-10 space-y-2">
                                    {renderCheckbox(
                                        cateringSelections?.salads?.orchardSaladAddChicken,
                                        "Add Chicken +$60"
                                    )}
                                    {renderCheckbox(
                                        cateringSelections?.salads?.orchardSaladAddSalmon,
                                        "Add Salmon +$80"
                                    )}
                                </div>
                            </>
                        ) : (
                            <p className="ml-8 text-gray-500 italic">Nothing selected</p>
                        )}
                    </div>
                </fieldset>

                {/* --- STARTERS --- */}
                <fieldset>
                    <legend className="text-xl font-semibold text-gray-900">Starters</legend>
                    <div className="mt-6 space-y-6 ml-8">
                        {hasAnyTrue(cateringSelections.starters) ? (
                            <>
                                {renderCheckbox(
                                    cateringSelections?.starters?.bavarianPretzels,
                                    "Bavarian Pretzels - $120",
                                    "40 soft pretzel sticks served with beer cheese"
                                )}
                                {renderCheckbox(
                                    cateringSelections?.starters?.chickenWings?.selected,
                                    "Chicken Wings - $70 for 50, $135 for 100, $230 for 200",
                                    "Served with 2 wing sauces on the side, plus 1 dressing"
                                )}
                                {cateringSelections?.starters?.chickenWings?.selected && (
                                    <>
                                        {cateringSelections?.starters?.chickenWings?.sauces?.length > 0 && (
                                            <div className="ml-8 space-y-1">
                                                <strong>Wing Sauces:</strong>
                                                <ul className="list-disc ml-6 text-sm text-gray-700">
                                                    {cateringSelections.starters.chickenWings.sauces.map((s, i) => (
                                                        <li key={i}>{s}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {cateringSelections?.starters?.chickenWings?.dressing && (
                                            <p className="ml-8 text-sm text-gray-700">
                                                <strong>Dressing:</strong> {cateringSelections.starters.chickenWings.dressing}
                                            </p>
                                        )}
                                    </>
                                )}

                                {renderCheckbox(
                                    cateringSelections?.starters?.chickenTenders?.selected,
                                    "Crispy Chicken Tenders - $150",
                                    "80 buttermilk soaked tenders, hand breaded, fried crispy, served with 2 sauces OR ranch"
                                )}
                                {cateringSelections?.starters?.chickenTenders?.selected && (
                                    <>
                                        <p className="ml-8 text-sm text-gray-700">
                                            <strong>Choice:</strong> {cateringSelections.starters.chickenTenders.choiceType}
                                        </p>
                                        {cateringSelections?.starters?.chickenTenders?.sauces?.length > 0 && (
                                            <div className="ml-8 space-y-1">
                                                <strong>Tender Sauces:</strong>
                                                <ul className="list-disc ml-6 text-sm text-gray-700">
                                                    {cateringSelections.starters.chickenTenders.sauces.map((s, i) => (
                                                        <li key={i}>{s}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                )}

                                {renderCheckbox(
                                    cateringSelections?.starters?.buffaloCauliflower,
                                    "Buffalo Cauliflower - $110",
                                    "Tender cauliflower florets, lightly dredged & fried crispy, served with buffalo wing sauce & ranch"
                                )}

                                {renderCheckbox(
                                    cateringSelections?.starters?.beerQuesoWithChips,
                                    "Beer Queso with Chips - $100",
                                    "Beer cheese, roasted Mexican street corn, garnished with cilantro, served with warm tortilla chips"
                                )}

                                {renderCheckbox(
                                    cateringSelections?.starters?.veggieCrudite,
                                    "Veggie Crudite - $75",
                                    "A variety of freshly sliced vegetables served with housemade ranch"
                                )}

                                {renderCheckbox(
                                    cateringSelections?.starters?.whippedHummusWithPita,
                                    "Whipped Hummus with Pita - $70",
                                    "Served with pita & drizzle of extra virgin olive oil"
                                )}

                                {cateringSelections?.starters?.flatbreadFlight?.selected && (
                                    <>
                                        {renderCheckbox(true, "Flatbread Flight - $100 (Westerville ONLY)")}
                                        <div className="ml-8 space-y-1">
                                            <strong>Selections:</strong>
                                            <ul className="list-disc ml-6 text-sm text-gray-700">
                                                {cateringSelections.starters.flatbreadFlight.selections.map((s, i) => (
                                                    <li key={i}>{s.replace(/-/g, " ")}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <p className="ml-8 text-gray-500 italic">Nothing selected</p>
                        )}
                    </div>
                </fieldset>

                {/* --- MAINS --- */}
                <fieldset>
                    <legend className="text-xl font-semibold text-gray-900">Mains</legend>
                    <div className="mt-6 space-y-6 ml-8">
                        {hasAnyTrue(cateringSelections.mains) ? (
                            <>
                                {renderCheckbox(
                                    cateringSelections?.mains?.blackenedSalmon,
                                    "BLACKENED SALMON - $300",
                                    "25 spiced Chilean salmon, served with choice of 1 side"
                                )}
                                {renderCheckbox(
                                    cateringSelections?.mains?.potRoast,
                                    "POT ROAST - $280",
                                    "Slow roasted, served with mashed potatoes & brown gravy"
                                )}
                                {renderCheckbox(
                                    cateringSelections?.mains?.chickenWraps,
                                    "CHICKEN WRAPS - $225",
                                    "25 wraps, cut in half. Grilled Caesar: grilled chicken, romaine, parmesan, creamy peppercorn Caesar dressing. Buffalo chicken: grilled or fried chicken, house-made buffalo sauce, lettuce, tomato, choose bleu cheese or ranch"
                                )}
                                {renderCheckbox(
                                    cateringSelections?.mains?.tacoBar,
                                    "TACO BAR - $200",
                                    "Choose from: ground beef or chicken, lettuce, tomato, salsa, cheese, flour tortillas (corn tortillas available upon request)"
                                )}
                                {renderCheckbox(
                                    cateringSelections?.mains?.herbSearedChicken,
                                    "HERB SEARED CHICKEN BREAST - $190",
                                    "Tender seared boneless breast coated in savory herbs and spices, served with choice of 1 side"
                                )}
                                {renderCheckbox(
                                    cateringSelections?.mains?.buffaloChickenShells,
                                    "BUFFALO CHICKEN SHELLS & CHEESE - $180",
                                    "House-made buffalo sauce, grilled chicken breasts, 3 cheese blend"
                                )}
                            </>
                        ) : (
                            <p className="ml-8 text-gray-500 italic">Nothing selected</p>
                        )}
                    </div>
                </fieldset>

                {/* --- SIDES --- */}
                <fieldset>
                    <legend className="text-xl font-semibold text-gray-900">Sides</legend>
                    <div className="mt-6 space-y-6 ml-8">
                        {hasAnyTrue(cateringSelections.sides) ? (
                            <>
                                {renderCheckbox(
                                    cateringSelections?.sides?.mashedPotatoes,
                                    "MASHED POTATOES - $70"
                                )}
                                {renderCheckbox(
                                    cateringSelections?.sides?.blisteredBrussels,
                                    "BLISTERED ROASTED BRUSSELS - $70"
                                )}
                                {renderCheckbox(
                                    cateringSelections?.sides?.roastedCarrots,
                                    "ROASTED CARROTS - $70"
                                )}
                                {renderCheckbox(
                                    cateringSelections?.sides?.sweetPotatoFries,
                                    "SWEET POTATO FRIES - $60"
                                )}
                                {renderCheckbox(
                                    cateringSelections?.sides?.frenchFries,
                                    "FRENCH FRIES - $50"
                                )}
                                {renderCheckbox(
                                    cateringSelections?.sides?.pitaBread,
                                    "PITA BREAD - $40",
                                    "25 pitas, served cut in quarters"
                                )}
                            </>
                        ) : (
                            <p className="ml-8 text-gray-500 italic">Nothing selected</p>
                        )}
                    </div>
                </fieldset>

                {/* --- SWEETS --- */}
                <fieldset>
                    <legend className="text-xl font-semibold text-gray-900">Sweets</legend>
                    <div className="mt-6 space-y-6 ml-8">
                        {hasAnyTrue(cateringSelections.sweets) ? (
                            <>
                                {renderCheckbox(
                                    cateringSelections?.sweets?.buckeyePieCups,
                                    "BUCKEYE PIE CUPS - $110",
                                    "Individually served. Chocolate graham cracker crust, whipped peanut butter filling, whipped cream topping, crushed peanut butter cups, and chocolate stout sauce."
                                )}
                                {renderCheckbox(
                                    cateringSelections?.sweets?.breadPudding,
                                    "BREAD PUDDING - $90",
                                    "Served warm, bourbon caramel sauce drizzle. Flavor varies — please ask!"
                                )}
                                {renderCheckbox(
                                    cateringSelections?.sweets?.turtleBrownies,
                                    "TURTLE BROWNIES - $90",
                                    "Served with berry compote, whipped cream, and toasted almonds."
                                )}
                            </>
                        ) : (
                            <p className="ml-8 text-gray-500 italic">Nothing selected</p>
                        )}
                    </div>
                </fieldset>

                {/* --- SPECIAL REQUESTS --- */}
                <fieldset>
                    <legend className="text-xl font-semibold text-gray-900">Special Requests</legend>
                    <p className="ml-8 text-gray-700 mt-2">
                        {cateringSelections.specialRequest?.trim()
                            ? cateringSelections.specialRequest
                            : "None"}
                    </p>
                </fieldset>
            </div>
        );
    };


    // PDF download
    const downloadPDF = async () => {
    const input = contentRef.current;
    if (!input) return;

    // Wait for all images to load
    const images = input.querySelectorAll('img');
    const imageLoadPromises = Array.from(images).map((img) => {
        if (!img.complete) {
            return new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
            });
        }
        return Promise.resolve();
    });
    await Promise.all(imageLoadPromises);

    // Generate canvas
    const canvas = await html2canvas(input, {
        useCORS: true,
        allowTaint: false,
        scale: 1.5,
        backgroundColor: '#ffffff',
        scrollY: -window.scrollY,
    });

    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const pdf = new jsPDF('p', 'mm', 'a4');

    const pixelWidth = canvas.width;
    const pixelHeight = canvas.height;

    const pdfWidth = imgWidth;
    const pdfHeight = (pixelHeight * pdfWidth) / pixelWidth;

    const pageHeightPx = (pageHeight * pixelHeight) / pdfHeight;

    let remainingHeight = pixelHeight;
    let pageIndex = 0;

    const bottomPaddingPx = 40;

    while (remainingHeight > 0) {
        const sliceHeight = Math.min(pageHeightPx - bottomPaddingPx, remainingHeight);

        // ✅ Use document from window
        const pageCanvas = window.document.createElement('canvas');
        pageCanvas.width = pixelWidth;
        pageCanvas.height = sliceHeight;

        const context = pageCanvas.getContext('2d');
        context.drawImage(
            canvas,
            0,
            pageIndex * (pageHeightPx - bottomPaddingPx),
            pixelWidth,
            sliceHeight,
            0,
            0,
            pixelWidth,
            sliceHeight
        );

        const imgData = pageCanvas.toDataURL('image/jpeg', 0.8);
        const imgHeight = (sliceHeight * pdfWidth) / pixelWidth;

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 10, pdfWidth, imgHeight);

        remainingHeight -= (pageHeightPx - bottomPaddingPx);
        pageIndex++;
    }

    pdf.save('NHBCatering.pdf');
};


    if (!termsData)
        return (
            <div className="text-center text-gray-600 py-10 text-lg">
                Loading catering document...
            </div>
        );

    const catering = termsData.nhbCatering ? { ...termsData, ...termsData.nhbCatering } : termsData;

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-6 flex justify-center">
            <div className="w-full max-w-7xl">
                {/* PDF Content */}
                <div
                    ref={contentRef}
                    className="bg-white rounded-lg shadow-xl p-10 space-y-12"
                >
                    {/* Logo & Title */}
                    <div className="flex flex-col items-center space-y-6">
                        <img
                            className="w-36 h-auto"
                            src="https://cohatch-media-assets.nyc3.cdn.digitaloceanspaces.com/static/images/NorthHigh_Full_Black.png"
                            alt="North High Logo"
                        />
                        <h1 className="text-4xl font-bold text-success-800 text-center">
                            Catering Order Form
                        </h1>
                    </div>

                    {/* Host & Event Info */}
                    <section className="space-y-6 border-b border-gray-200 pb-10">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Host & Event Information
                        </h2>

                        <div className="grid max-w-7xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2 text-gray-800">
                            <div className="sm:col-span-3">
                                <label className="block text-sm font-medium text-gray-500">
                                    First Name
                                </label>
                                <p className="mt-1 text-base font-semibold text-gray-900">
                                    {termsData?.firstName || "—"}
                                </p>
                            </div>

                            <div className="sm:col-span-3">
                                <label className="block text-sm font-medium text-gray-500">
                                    Last Name
                                </label>
                                <p className="mt-1 text-base font-semibold text-gray-900">
                                    {termsData?.lastName || "—"}
                                </p>
                            </div>

                            <div className="sm:col-span-3">
                                <label className="block text-sm font-medium text-gray-500">
                                    Email Address
                                </label>
                                <p className="mt-1 text-base font-semibold text-gray-900 break-words">
                                    {termsData?.emailAddress || "—"}
                                </p>
                            </div>

                            <div className="sm:col-span-3">
                                <label className="block text-sm font-medium text-gray-500">
                                    Phone Number
                                </label>
                                <p className="mt-1 text-base font-semibold text-gray-900">
                                    {termsData?.phoneNumber || "—"}
                                </p>
                            </div>

                            <div className="sm:col-span-6">
                                <label className="block text-sm font-medium text-gray-500">
                                    Event Name
                                </label>
                                <p className="mt-1 text-base font-semibold text-gray-900">
                                    {termsData?.nhbCatering?.eventName || "—"}
                                </p>
                            </div>

                            <div className="sm:col-span-2 sm:col-start-1">
                                <label className="block text-sm font-medium text-gray-500">
                                    Approximate Guest Count
                                </label>
                                <p className="mt-1 text-base font-semibold text-gray-900">
                                    {termsData?.nhbCatering?.guestCount || "—"}
                                </p>
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-500">
                                    Date Sent
                                </label>
                                <p className="mt-1 text-base font-semibold text-gray-900">
                                    {termsData?.nhbCatering?.dateSent
                                        ? new Date(termsData.nhbCatering.dateSent).toLocaleDateString("en-US", { timeZone: "UTC" })
                                        : "—"}
                                </p>
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-500">
                                    Catering Deadline
                                </label>
                                <p className="mt-1 text-base font-semibold text-gray-900">
                                    {termsData?.nhbCatering?.cateringDeadline
                                        ? new Date(termsData.nhbCatering.cateringDeadline).toLocaleDateString("en-US", { timeZone: "UTC" })
                                        : "—"}
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Catering Selections */}
                    <section>
                        {termsData?.cateringSelections ? (
                            <ReadOnlyCateringForm cateringSelections={termsData.cateringSelections} />
                        ) : (
                            <p className="text-center text-gray-500 italic mt-10">
                                No catering selections found for this form.
                            </p>
                        )}
                    </section>

                    {/* Footer */}
                    <footer className="text-center text-sm text-gray-600 space-y-1 pt-10 border-t border-gray-200">
                        <p>Thank you!</p>
                        <p>Upon submitting this form, we will review your order.</p>
                        <p>You will receive a BEO from the Event Manager within 3 business days as confirmation.</p>
                        <p>Payment due at the conclusion of your event.</p>
                    </footer>
                </div>

                {/* Download Button */}
                <div className="mt-8 text-center">
                    <button
                        onClick={downloadPDF}
                        className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold py-2 px-6 rounded transition duration-150"
                    >
                        Download PDF
                    </button>
                </div>
            </div>
        </div>
    );
}
